import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { parseContentDispositionFilename } from '../core/api-response-utils';
import type {
  AdvanceBillingCyclesRequest,
  CreateSubscriptionRequest,
  PublicSubscriptionCheckoutRequest,
  PublicSubscriptionConfirm3DSRequest,
  SATCFDIUseDTO,
  SubscriptionDTO,
  UpdateSubscriptionRequest,
  WrapperResponseAdvanceBillingCyclesResultDTO,
  WrapperResponseSubscriptionDTO,
  WrapperResponseListSubscriptionDTO,
  WrapperPageableResponseSubscriptionDTO,
  WrapperResponse,
  WrapperResponsePublicSubscriptionDetailDTO,
  WrapperResponseVoid,
  SearchSubscriptionFilterSpec,
  ListSubscriptionFilterSpec
} from '../core/models';
import type { Pageable, WrapperPageableResponse } from './service-common';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  searchSubscriptions(
    spec: SearchSubscriptionFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperResponseListSubscriptionDTO> {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));
    if (pageable.sort?.length) {
      pageable.sort.forEach(s => { params = params.append('sort', s); });
    }
    return this.http.get<WrapperResponseListSubscriptionDTO>(`${this.apiUrl}/subscriptions`, { params });
  }

  listSubscriptions(
    spec: ListSubscriptionFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponseSubscriptionDTO> {
    let params = this.buildSpecParams(spec);
    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));
    if (pageable.sort?.length) {
      pageable.sort.forEach(s => { params = params.append('sort', s); });
    }
    return this.http.get<WrapperPageableResponseSubscriptionDTO>(`${this.apiUrl}/subscriptions/list`, { params });
  }

  exportSubscriptionsCsv(
    spec: ListSubscriptionFilterSpec = {},
    sort: string[] = ['createdAt,desc']
  ): Observable<{ blob: Blob; filename: string }> {
    let params = this.buildSpecParams(spec);
    sort.forEach((s) => {
      params = params.append('sort', s);
    });
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      params = params.append('timeZone', timeZone);
    }

    return this.http
      .get(`${this.apiUrl}/subscriptions/export`, {
        params,
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map((resp) => ({
          blob: resp.body as Blob,
          filename:
            parseContentDispositionFilename(resp.headers.get('Content-Disposition')) ?? 'suscripciones.csv'
        }))
      );
  }

  private buildSpecParams(spec: ListSubscriptionFilterSpec): HttpParams {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null && v !== '') {
            params = params.append(key, String(v));
          }
        });
      } else if (value !== '') {
        params = params.append(key, String(value));
      }
    });
    return params;
  }

  getSubscriptionById(id: number): Observable<WrapperResponseSubscriptionDTO> {
    return this.http.get<WrapperResponseSubscriptionDTO>(`${this.apiUrl}/subscriptions/${id}`);
  }

  createSubscription(request: CreateSubscriptionRequest): Observable<WrapperResponseSubscriptionDTO> {
    return this.http.post<WrapperResponseSubscriptionDTO>(`${this.apiUrl}/subscriptions`, request);
  }

  updateSubscription(
    id: number,
    request: UpdateSubscriptionRequest
  ): Observable<WrapperResponseSubscriptionDTO> {
    return this.http.put<WrapperResponseSubscriptionDTO>(`${this.apiUrl}/subscriptions/${id}`, request);
  }

  cancelSubscription(id: number): Observable<WrapperResponseVoid> {
    return this.http.post<WrapperResponseVoid>(`${this.apiUrl}/subscriptions/${id}/cancel`, {});
  }

  deleteSubscription(id: number): Observable<WrapperResponseVoid> {
    return this.http.delete<WrapperResponseVoid>(`${this.apiUrl}/subscriptions/${id}`);
  }

  previewAdvanceBillingCycles(
    id: number,
    request: AdvanceBillingCyclesRequest
  ): Observable<WrapperResponseAdvanceBillingCyclesResultDTO> {
    return this.http.post<WrapperResponseAdvanceBillingCyclesResultDTO>(
      `${this.apiUrl}/subscriptions/${id}/advance-billing-cycles/preview`,
      request
    );
  }

  advanceBillingCycles(
    id: number,
    request: AdvanceBillingCyclesRequest
  ): Observable<WrapperResponseAdvanceBillingCyclesResultDTO> {
    return this.http.post<WrapperResponseAdvanceBillingCyclesResultDTO>(
      `${this.apiUrl}/subscriptions/${id}/advance-billing-cycles`,
      request
    );
  }

  // ── Endpoints públicos (sin autenticación) ──

  /**
   * Hash público de una suscripción de cargo recurrente. No caduca.
   */
  createSubscriptionPublicHash(subscriptionId: number): Observable<WrapperResponse<string>> {
    return this.http.get<WrapperResponse<string>>(
      `${this.apiUrl}/subscriptions/${subscriptionId}/public-hash`
    );
  }

  getPublicSubscription(token: string, hash?: string | null): Observable<WrapperResponsePublicSubscriptionDetailDTO> {
    let params = new HttpParams();
    if (hash) {
      params = params.set('hash', hash);
    }
    return this.http.get<WrapperResponsePublicSubscriptionDetailDTO>(
      `${this.apiUrl}/public/subscriptions/${token}`,
      { params }
    );
  }

  listPublicSatCfdiUses(
    token: string,
    pageable: Pageable = { page: 0, size: 500 }
  ): Observable<WrapperPageableResponse<SATCFDIUseDTO>> {
    let params = new HttpParams()
      .append('page', String(pageable.page))
      .append('size', String(pageable.size));
    if (pageable.sort?.length) {
      pageable.sort.forEach(s => { params = params.append('sort', s); });
    }
    return this.http.get<WrapperPageableResponse<SATCFDIUseDTO>>(
      `${this.apiUrl}/public/subscriptions/${token}/sat-cfdi-uses`,
      { params }
    );
  }

  checkoutSubscription(
    token: string,
    request: PublicSubscriptionCheckoutRequest
  ): Observable<WrapperResponseSubscriptionDTO> {
    return this.http.post<WrapperResponseSubscriptionDTO>(
      `${this.apiUrl}/public/subscriptions/${token}/checkout`,
      request
    );
  }

  /**
   * Checkout público para suscripciones con proveedor Paycode.
   * Envía los datos de tarjeta (o tarjeta guardada), teléfono y activa la suscripción si el cobro es exitoso.
   */
  checkoutPaycodeSubscription(
    token: string,
    request: PublicSubscriptionCheckoutRequest
  ): Observable<WrapperResponseSubscriptionDTO> {
    return this.http.post<WrapperResponseSubscriptionDTO>(
      `${this.apiUrl}/public/subscriptions/paycode/${token}/checkout`,
      request
    );
  }

  /**
   * Confirma una suscripción que requirió 3D Secure y ya pasó el challenge en el cliente.
   * El backend invoca el endpoint de confirmación de NetPay y activa la suscripción.
   */
  confirmSubscription3DS(
    token: string,
    request: PublicSubscriptionConfirm3DSRequest
  ): Observable<WrapperResponseSubscriptionDTO> {
    return this.http.post<WrapperResponseSubscriptionDTO>(
      `${this.apiUrl}/public/subscriptions/${token}/confirm-3ds`,
      request
    );
  }

  /**
   * Elimina (soft-delete) una tarjeta guardada del cliente desde la página pública de
   * suscripción. La autorización se prueba con el {@code token} de la suscripción; el
   * backend valida que la tarjeta pertenezca al mismo cliente.
   */
  deletePublicCustomerCard(
    token: string,
    customerCardId: number
  ): Observable<WrapperResponseVoid> {
    return this.http.delete<WrapperResponseVoid>(
      `${this.apiUrl}/public/subscriptions/${token}/cards/${customerCardId}`
    );
  }
}
